import { useRef, useState } from 'react'
import { Space } from '../../Hooks/useRecommendation/useSpaceRecommendations'
import { LeftIcon, RightIcon, LocationIcon, PeopleIcon } from '../../Views/Success/LeadFeed/Icons'

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
interface VenueSearchCardProps {
  space: Space
  isSelected: boolean
  onToggle: () => void
  onCardClick?: () => void
}

const VenueSearchCard = ({
  space,
  isSelected,
  onToggle,
  onCardClick,
}: VenueSearchCardProps): JSX.Element => {
  const [imageIndex, setImageIndex] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)
  const [frozenImageHeight, setFrozenImageHeight] = useState<number | undefined>(undefined)
  const imageContainerRef = useRef<HTMLDivElement>(null)

  return (
    <div
      onClick={onCardClick}
      className={`bg-white border border-[#bfbfbf] rounded-lg overflow-hidden flex flex-col
        sm:flex-row sm:items-stretch sm:gap-[19px] sm:p-4 transition-colors ${
          onCardClick ? 'cursor-pointer' : ''
        }`}>
      <div
        ref={imageContainerRef}
        className={`relative shrink-0 w-full h-48 sm:w-[250px] sm:h-auto overflow-hidden sm:rounded-xl ${
          frozenImageHeight ? 'sm:self-start' : ''
        }`}
        style={frozenImageHeight ? { height: frozenImageHeight } : undefined}>
        {space.images.length > 0 && (
          <img
            src={space.images[imageIndex]}
            alt={`${space.title}, ${space.venueName}`}
            className='absolute inset-0 w-full !h-full object-cover'
          />
        )}
        {space.images.length > 1 && (
          <>
            <div className='absolute bottom-3 left-3 right-3 flex items-center justify-between pointer-events-none'>
              {imageIndex > 0 ? (
                <button
                  type='button'
                  className='bg-white/90 backdrop-blur-sm rounded-xl p-1 cursor-pointer shadow-sm pointer-events-auto'
                  onClick={(e) => {
                    e.stopPropagation()
                    setImageIndex(imageIndex - 1)
                  }}
                  aria-label='Previous photo'>
                  <LeftIcon width={24} height={24} />
                </button>
              ) : (
                <div />
              )}
              {imageIndex < space.images.length - 1 ? (
                <button
                  type='button'
                  className='bg-white/90 backdrop-blur-sm rounded-xl p-1 cursor-pointer shadow-sm pointer-events-auto'
                  onClick={(e) => {
                    e.stopPropagation()
                    setImageIndex(imageIndex + 1)
                  }}
                  aria-label='Next photo'>
                  <RightIcon width={24} height={24} />
                </button>
              ) : (
                <div />
              )}
            </div>
            <div
              className={`absolute top-3 right-3 bg-black/55 text-white text-xs font-medium rounded-full
                px-2 py-0.5 pointer-events-none`}>
              {imageIndex + 1} / {space.images.length}
            </div>
          </>
        )}
      </div>

      <div className='flex-1 min-w-0 flex flex-col p-4 sm:py-[13px] sm:pr-4 sm:pl-0 gap-2'>
        <div className='flex flex-col gap-2'>
          <h3 className='!text-lg font-semibold text-black leading-6 line-clamp-1 !mb-0'>
            {space.title}, {space.venueName}
          </h3>
          <div className='flex items-center gap-1 text-black'>
            <LocationIcon width={16} height={16} className='shrink-0' />
            <span className='text-base tracking-[0.075px]'>
              {space.location.city}, {space.location.country}
            </span>
          </div>
        </div>

        <div className='flex items-center gap-4 text-black'>
          {(space.standingCapacity ?? 0) > 0 || (space.seatingCapacity ?? 0) > 0 ? (
            <>
              {(space.standingCapacity ?? 0) > 0 && (
                <div className='flex items-center gap-1'>
                  <PeopleIcon width={16} height={16} className='w-4 h-4 shrink-0' />
                  <span className='text-md'>{space.standingCapacity} standing</span>
                </div>
              )}
              {(space.seatingCapacity ?? 0) > 0 && (
                <div className='flex items-center gap-1'>
                  <PeopleIcon width={16} height={16} className='w-4 h-4 shrink-0' />
                  <span className='text-md'>{space.seatingCapacity} seated</span>
                </div>
              )}
            </>
          ) : space.capacity > 0 ? (
            <div className='flex items-center gap-1'>
              <PeopleIcon width={16} height={16} className='w-4 h-4 shrink-0' />
              <span className='text-md'>{space.capacity} capacity</span>
            </div>
          ) : null}
        </div>

        <p className={`text-md text-black font-normal ${isExpanded ? '!my-4' : 'line-clamp-3'}`}>
          {space.description}
        </p>

        <button
          type='button'
          onClick={() => {
            if (!isExpanded) {
              setFrozenImageHeight(imageContainerRef.current?.offsetHeight)
            } else {
              setFrozenImageHeight(undefined)
            }
            setIsExpanded(!isExpanded)
          }}
          className='text-[#595959] text-base cursor-pointer hover:text-[#af231c] transition-colors text-left'>
          {isExpanded ? 'See less' : 'See more'}
        </button>

        <button
          type='button'
          onClick={onToggle}
          className={`flex items-center justify-between h-8 w-full sm:w-[180px] rounded-[4px] !px-12 sm:!px-2 ${
            isSelected ? 'bg-[#AF231C] hover:bg-[#8b1a15]' : 'bg-black hover:bg-[#333333]'
          }
            text-white font-semibold text-sm tracking-[0.07px] transition-all cursor-pointer`}>
          <span className='w-[14px]' />
          <span>{isSelected ? 'Selected' : 'Request quote'}</span>
          <ArrowRightAltIcon width={14} height={14} className='shrink-0' />
        </button>
      </div>
    </div>
  )
}

export default VenueSearchCard
